import type { SeriesClickCallbackFunction, TitleOptions } from 'highcharts';
import { useMemo } from 'react';

interface PieChartOptions {
  donut?: boolean;
  onClick?: SeriesClickCallbackFunction;
  showAsPercentage?: boolean;
  label?: {
    total: string;
    description: string;
  };
}

const getLabelHtml = (
  donut: boolean | undefined,
  label: PieChartOptions['label']
) => {
  if (donut && label) {
    return `<span style="font-weight: 700">${label?.total}</span><br/><span style="font-size: 75%">${label?.description}</span>`;
  }

  return '';
};

const getTitleOptions = (
  donut: boolean | undefined,
  label: PieChartOptions['label']
): TitleOptions => {
  return {
    verticalAlign: 'middle',
    align: 'center',
    floating: true,
    text: getLabelHtml(donut, label),
  };
};

export const useGetPieChartDefaultOptions = ({
  donut,
  onClick,
  showAsPercentage,
  label,
}: PieChartOptions): Highcharts.Options => {
  return useMemo(() => {
    const options: Highcharts.Options = {
      chart: {
        type: 'pie',
      },
      exporting: {
        chartOptions: {
          title: getTitleOptions(donut, label),
        },
        filename: 'chart',
      },
      title: getTitleOptions(donut, label),
      plotOptions: {
        pie: {
          size: '75%',
          innerSize: donut ? '75%' : '0',
          center: ['50%', '50%'], // force chart to be centered to ensure label is positioned correctly
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format: `{point.name}<br />${showAsPercentage ? '{point.percentage:.0f}%' : '{point.y}'}`,
            style: {
              textOverflow: 'none',
            },
          },
          events: {
            click: onClick,
          },
        },
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 510,
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  dataLabels: {
                    enabled: false,
                  },
                },
              },
            },
          },
        ],
      },
      tooltip: {
        headerFormat: '<b>{point.name}</b><br/>',
        pointFormat:
          'Count: {point.y}<br/> Percentage: {point.percentage:.1f}%',
      },
    };

    return options;
  }, [label, donut, onClick, showAsPercentage]);
};
